import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { formatLinkBlockToUrl, formatUrlWithActiveFund } from '$utilities/format';
import { isLocalURL } from '$utilities/browser';
import { useKeyboard } from '$hooks/events';
import { IconClose } from '$partials/icons';
import { getSiteContext } from '$utilities/site-context';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const HeaderDialog = ({
    isNavOpen,
    closeNav,
    links,
    funds,
}) => {
    const [animationOut, setAnimationOut] = useState(false);
    const dialogRef = useRef();
    const scrollElementRef = useRef();

    const handleClose = () => {

        setAnimationOut(true);

        setTimeout(() => {
            closeNav(false);
        }, 250);
    };

    useKeyboard({
        'Escape': () => isNavOpen && handleClose()
    }, [isNavOpen]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;
        if (isNavOpen) {
            if (dialogNode && !dialogNode.open && scrollElementNode) {
                disableBodyScroll(dialogNode);
                disableBodyScroll(scrollElementNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open && scrollElementNode) {
                enableBodyScroll(dialogNode);
                enableBodyScroll(scrollElementNode);
                dialogNode.close?.();
                setAnimationOut(false);
            }
        }

        return () => {
            enableBodyScroll(dialogNode);
            enableBodyScroll(scrollElementNode);
        };
    }, [isNavOpen]);

    return (
        <dialog
            className={`dialog-reset backdrop-blur-in ${animationOut ? 'blur-out' : ''}`}
            onClick={handleClose}
            ref={dialogRef}>
            <div
                className={`w-full overflow-x-scroll fixed top-0 bottom-0 left-0 min-h-dynamic-screen slide-x-fade-in ${animationOut ? 'slide-out' : ''}`}
                ref={scrollElementRef}>
                <div className='rounded-3xl bg-white md:bg-transparent layout-container mt-60 sm:mt-45 md:mt-0 md:py-10'>
                    <div className='flex flex-wrap justify-end h-full layout-negative-margin'>
                        <div
                            className='w-full md:w-5/12 layout-item-padding'
                            /* `stopPropagation()` to not close dialog when dialog is clicked */
                            onClick={(e) => e.stopPropagation()}>
                            <div className='md:bg-white relative h-full md:-mr-125 rounded-3xl'>
                                {/* Close button */}
                                <button
                                    aria-expanded={isNavOpen}
                                    aria-label={'Close main navigation'}
                                    className='block rounded-full absolute right-0 top-15 md:right-50 md:top-55 md:mt-[2px] bg-grey p-12'
                                    onClick={handleClose}
                                    type='button'>
                                    <IconClose />
                                </button>

                                <div className='pt-55 md:pt-100  md:pr-125'>
                                    <div className='md:pl-1/5'>
                                        {/* Main navigation links */}
                                        {links?.length > 0 && (
                                            <nav
                                                aria-label='Main navigation links'
                                                className='flex flex-col gap-y-30 md:gap-y-40'
                                                id='main-navigation'>
                                                {links.map((link, i) => {
                                                    const href = formatUrlWithActiveFund(formatLinkBlockToUrl(link.link));
                                                    const target = isLocalURL(href) ? '_self' : '_blank';
                                                    const rel = isLocalURL(href) ? '' : 'noreferrer noopener';

                                                    return (
                                                        <a
                                                            className='md:ml-5 self-start type-style-3'
                                                            href={href}
                                                            key={i}
                                                            rel={rel}
                                                            target={target}>
                                                            {link.label || link.link?.page?.title }
                                                        </a>
                                                    );
                                                })}
                                            </nav>
                                        )}
                                        {/* Funds links */}
                                        {funds?.length > 0 && (
                                            <>
                                                {/* Separator line */}
                                                <div className='h-1 w-full bg-black/10 block mt-30 mb-40 md:my-40'/>
                                                <nav
                                                    aria-label='Secondary navigation'
                                                    className='flex flex-col gap-y-30 md:gap-y-40 pb-60'
                                                    id='secondary-navigation'>
                                                    {funds.map((link, i) => {
                                                        const href = formatLinkBlockToUrl(link.link);
                                                        const target = isLocalURL(href) ? '_self' : '_blank';
                                                        const rel = isLocalURL(href) ? '' : 'noreferrer noopener';
                                                        const activeFundSlug = getSiteContext().fundSlug;
                                                        const fundUrl = activeFundSlug ? `/${activeFundSlug}` : '/';

                                                        if (fundUrl && fundUrl === href && link.link?.page?.__typename === 'FundPage') {
                                                            return null;
                                                        }
                                                        return (
                                                            <a
                                                                className='md:ml-5 self-start type-style-4'
                                                                href={href}
                                                                key={i}
                                                                rel={rel}
                                                                target={target}>
                                                                {link.label || link.link?.page?.title }
                                                            </a>
                                                        );
                                                    })}
                                                </nav>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </dialog>
    );
};
/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const navigationLink = PropTypes.shape({
    page: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
    }),
});
const propTypes = {
    closeNav: PropTypes.func.isRequired,
    isNavOpen: PropTypes.bool,
    links: PropTypes.arrayOf(navigationLink),
    funds: PropTypes.arrayOf(navigationLink),
};

HeaderDialog.propTypes = propTypes;

export default HeaderDialog;
